/**
 * MainLayout Component
 * AIDLC Administrative Backend
 *
 * Root layout wrapping all authenticated pages.
 * Structure per Maya's UI Design System:
 *   [Sidebar 220px] [Content Area (scrollable)]
 *   [Header 48px fixed at top of content area]
 *   [Main content below header, scrollable]
 *
 * This component also initializes the WebSocket connection,
 * passes connection state and bridge status to the Header indicator,
 * and manages the interactive guided tour overlay.
 *
 * Bridge integration: The socket instance is stored in React context
 * via BridgeContext so that child components (like AgentDashboard)
 * can subscribe to real-time agent status updates.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useTour } from '../../hooks/useTour';
import { GuidedTour, TOUR_STEPS } from '../tour/GuidedTour';
import { useAgents } from '../../hooks/useAgents';
import type { Socket } from 'socket.io-client';

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

// =============================================
// Bridge Context - provides socket to children
// =============================================

interface BridgeContextValue {
  socket: Socket | null;
  isConnected: boolean;
  bridgeActive: boolean;
  lastBridgeUpdate: string | null;
}

const BridgeContext = createContext<BridgeContextValue>({
  socket: null,
  isConnected: false,
  bridgeActive: false,
  lastBridgeUpdate: null,
});

export function useBridge(): BridgeContextValue {
  return useContext(BridgeContext);
}

// =============================================
// MainLayout Component
// =============================================

export function MainLayout() {
  // Acquire MSAL access token for WebSocket auth (production only)
  const [wsToken, setWsToken] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (DEV_AUTH_BYPASS) return;
    let cancelled = false;
    (async () => {
      try {
        const { ensureMsalInitialized } = await import('../../msalInstance');
        const { apiRequest } = await import('../../authConfig');
        const msalInstance = await ensureMsalInitialized();
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0 && !cancelled) {
          const resp = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });
          if (resp.accessToken && !cancelled) {
            setWsToken(resp.accessToken);
          }
        }
      } catch (err) {
        console.error('[MainLayout] Failed to acquire token for WebSocket:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { isConnected, socket } = useWebSocket({
    token: wsToken,
    autoConnect: DEV_AUTH_BYPASS || !!wsToken,
  });
  const tour = useTour(TOUR_STEPS.length);

  // Initialize useAgents at layout level with socket for bridge detection
  // This gives us bridge status for the header without requiring AgentDashboard to be mounted
  const { bridgeActive, lastBridgeUpdate } = useAgents(
    60000, // Longer poll at layout level (60s), AgentDashboard polls faster
    socket as BridgeContextValue['socket'],
  );

  const bridgeContextValue: BridgeContextValue = {
    socket,
    isConnected,
    bridgeActive,
    lastBridgeUpdate,
  };

  return (
    <BridgeContext.Provider value={bridgeContextValue}>
      <div className="min-h-screen bg-gray-50">
        {/* Fixed Sidebar */}
        <Sidebar />

        {/* Content Area -- offset by sidebar width */}
        <div className="ml-[220px]">
          {/* Fixed Header */}
          <Header
            isWsConnected={isConnected}
            bridgeActive={bridgeActive}
            lastBridgeUpdate={lastBridgeUpdate}
            onStartTour={tour.startTour}
            socket={socket}
          />

          {/* Scrollable Main Content -- offset by header height */}
          <main className="pt-12 min-h-screen">
            <div className="p-6">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Guided Tour Overlay */}
        <GuidedTour
          isActive={tour.isActive}
          currentStep={tour.currentStep}
          totalSteps={tour.totalSteps}
          onNext={tour.nextStep}
          onPrev={tour.prevStep}
          onSkip={tour.skipTour}
          onGoToStep={tour.goToStep}
          onComplete={tour.completeTour}
        />
      </div>
    </BridgeContext.Provider>
  );
}
